import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Wrench, ArrowLeft, CreditCard, Calendar, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const PaymentHistory = () => {
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchTransactions();
    }
  }, [token]);

  const fetchTransactions = async () => {
    try {
      const res = await axios.get(`${API}/payments/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(res.data);
    } catch (error) {
      toast.error('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-[#009B4D]/10 text-[#009B4D]';
      case 'pending':
        return 'bg-[#FFB81C]/10 text-[#FFB81C]';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      {/* Header */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              data-testid="back-btn"
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 text-[#003B5C] hover:text-[#002A42] transition-colors duration-300"
            >
              <ArrowLeft className="w-5 h-5" />
              <Wrench className="w-8 h-8" />
              <span className="text-2xl font-bold font-['Outfit']">SkillYaad</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#003B5C] mb-2">Payment History</h1>
          <p className="text-[#475569]">View all your payment transactions</p>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 border-4 border-[#003B5C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[#475569]">Loading transactions...</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-[#94A3B8] text-lg">No payment transactions yet</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F9F7F2]">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-[#0F172A]">Date</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-[#0F172A]">Type</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-[#0F172A]">Amount</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-[#0F172A]">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-[#0F172A]">Transaction ID</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {transactions.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-[#F9F7F2]/50 transition-colors duration-200">
                      <td className="px-6 py-4 text-sm text-[#475569]">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-2" />
                          {new Date(transaction.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#0F172A] font-medium capitalize">
                        {transaction.payment_type.replace('_', ' ')}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center text-[#003B5C] font-bold font-['Space_Grotesk']">
                          <DollarSign className="w-4 h-4" />
                          {transaction.currency} ${transaction.amount.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(transaction.payment_status)}`}>
                          {transaction.payment_status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#94A3B8] font-mono">
                        {transaction.session_id.slice(0, 20)}...
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;